import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { useToast } from '@/hooks/use-toast';
import { useErrorHandler } from '@/utils/errorHandling/centralizedErrorHandler';
import { Database } from '@/types/supabase';

type TableName = keyof Database['public']['Tables'];

interface Entity {
  id: string;
}


interface UseEntityOperationsProps<T extends Entity, TTableName extends TableName> {
  tableName: TTableName;
  queryKey: (string | { [key: string]: unknown } | undefined)[];
  entityToInsertDbMapper: (entity: Partial<Omit<T, 'id'>>) => Database['public']['Tables'][TTableName]['Insert'];
  entityToUpdateDbMapper: (entity: Partial<Omit<T, 'id'>>) => Database['public']['Tables'][TTableName]['Update'];
  dbEntityToEntityMapper: (dbEntity: Database['public']['Tables'][TTableName]['Row']) => T;
}

export const useEntityOperations = <
  T extends Entity,
  TTableName extends TableName
>({
  tableName,
  queryKey,
  entityToInsertDbMapper,
  entityToUpdateDbMapper,
  dbEntityToEntityMapper,
}: UseEntityOperationsProps<T, TTableName>) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  const mutationOptions = {
    onSuccess: (operation: 'create' | 'update' | 'delete') => {
      queryClient.invalidateQueries({ queryKey });
      const messages = {
        create: 'Запись успешно создана',
        update: 'Запись успешно обновлена',
        delete: 'Запись успешно удалена',
      };
      toast({ title: 'Успех', description: messages[operation] });
    },
    onError: (error: unknown, operation: string) => {
      handleError(error, { operation: `useEntityOperations.${tableName}.${operation}` });
    },
  };

  const { mutateAsync: createEntity, isPending: isCreating } = useMutation<
    T,
    Error,
    Partial<Omit<T, 'id'>>
  >({ 
    mutationFn: async (entityData) => {
      const dbData = entityToInsertDbMapper(entityData as Partial<Omit<T, 'id'>>);
      const { data, error } = await supabaseWorker
        .from(tableName)
        .insert(dbData)
        .select()
        .single();

      if (error) throw error;
      return dbEntityToEntityMapper(
        data as Database['public']['Tables'][TTableName]['Row']
      );
    },
    onSuccess: () => mutationOptions.onSuccess('create'),
    onError: (error) => mutationOptions.onError(error, 'create'),
  });

  const { mutateAsync: updateEntity, isPending: isUpdating } = useMutation<
    T,
    Error,
    { id: string; updates: Partial<Omit<T, 'id'>> }
  >({
    mutationFn: async ({ id, updates }) => {
      console.log(`[useEntityOperations.${tableName}] updateEntity called with:`, { id, updates });
      if (!id) {
        const err = new Error('Update failed: missing entity id');
        console.error(`[useEntityOperations.${tableName}]`, err);
        throw err;
      }
      const dbUpdates = entityToUpdateDbMapper(updates as Partial<Omit<T, 'id'>>);
      console.log(`[useEntityOperations.${tableName}] Mapped dbUpdates:`, dbUpdates);
      
      const { data, error } = await supabaseWorker
        .from(tableName)
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .maybeSingle();

      console.log(`[useEntityOperations.${tableName}] Supabase update result:`, { data, error });
      if (error) {
        console.error(`[useEntityOperations.${tableName}] Update error:`, error);
        throw error;
      }
      if (!data) {
        const err = new Error(`Update failed: no row returned for id ${id}`);
        console.error(`[useEntityOperations.${tableName}]`, err);
        throw err;
      }
      const result = dbEntityToEntityMapper(
        data as Database['public']['Tables'][TTableName]['Row']
      );
      console.log(`[useEntityOperations.${tableName}] Final mapped result:`, result);
      return result;
    },
    onSuccess: () => mutationOptions.onSuccess('update'),
    onError: (error) => mutationOptions.onError(error, 'update'),
  });
    

  const { mutateAsync: deleteEntity, isPending: isDeleting } = useMutation<
    string,
    Error,
    string
  >({
    mutationFn: async (id: string) => {
      console.log(`[useEntityOperations.${tableName}] deleteEntity called with id:`, id);
      // ИСПРАВЛЕНО: Упрощенная типизация для устранения бесконечной инстанциации типов
      const { error } = await supabaseWorker.from(tableName as string).delete().eq('id', id);
      console.log(`[useEntityOperations.${tableName}] Supabase delete result:`, { error });
      if (error) {
        console.error(`[useEntityOperations.${tableName}] Delete error:`, error);
        throw error;
      }
      console.log(`[useEntityOperations.${tableName}] Delete completed successfully for id:`, id);
      return id;
    },
    onSuccess: () => mutationOptions.onSuccess('delete'),
    onError: (error) => mutationOptions.onError(error, 'delete'),
  });

  return {
    createEntity,
    updateEntity,
    deleteEntity,
    isCreating,
    isUpdating,
    isDeleting,
  };
};
